/**
 *
 * SideList
 *
 */

import React, {
  memo,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import {
  Layout,
  List,
  PageHeader,
  Input,
  Checkbox,
  Collapse,
  Empty,
  Button,
} from "antd";
const { Sider } = Layout;
const { Panel } = Collapse;

// icons
import CustomIcons from "components/CustomIcons";
import { CloseOutlined, PlusOutlined, UndoOutlined } from "@ant-design/icons";

// components
import SideListItem from "components/SideListItem";

// utils
import history from "utils/history";
import { getObjId } from "utils/helpers/helper.general.js";
import {
  sortList,
  searchListBy,
  prepearFilteredOptions,
  filterListBy,
} from "utils/helpers/helper.filter-sort";
// utils
import { USER_TYPES_OPTIONS } from 'utils/helpers/helper.constant';

function SideList({
  type,
  title,
  subTitle,
  sortProps,
  searchProps,
  filterProps,
  ascend,
  outputListItem,
  showPreventClass,
  // main
  list,
  currentItem,
  currentItemAction,
  // actions
  addAction,
  onClick,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames("side-list-wrapper", className);
  const { t, i18n } = useTranslation();

  const initSort = {
    ascend: ascend || false, // descend
    props: sortProps ? [...new Set([...sortProps])] : ["createdAt"], // 'description.firstname'
  };
  const initSearch = searchProps
    ? Array.isArray(searchProps)
      ? [...new Set([...searchProps])]
      : searchProps
    : "";
  const initFilter =
    filterProps &&
    (Array.isArray(filterProps) ? [...new Set([...filterProps])] : filterProps);

  const [showSearch, setShowSearch] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  const [searchVal, setSearchVal] = useState("");
  const searchRef = useRef(null);
  const [sort, setSort] = useState(initSort);
  const [filter, setFilter] = useState([]);
  const [filterOptions, setFilterOptions] = useState("");
  const [renderList, setRenderList] = useState(list || []);
  const [activeListItem, setActiveListItem] = useState(currentItem);

  // set active list Item
  useEffect(() => {
    if (currentItem) {
      setFirstElemInArray(currentItem);
    } else if (!currentItem && (renderList?.length > 0 || list?.length > 0)) {
      const initList = renderList?.length > 0 ? renderList : list;
      const sortedList = getSortedFilteredList(initList);
      const firstElem = sortedList[0];
      setFirstElemInArray(firstElem);
    }
  }, [currentItem]);

  // Scroll to Active list element
  useEffect(() => {
    let timeOutListScroll;
    if (activeListItem || currentItem) {
      const elemID = activeListItem
        ? getObjId(activeListItem)
        : getObjId(currentItem);
      const domListElement = document.getElementById(`${type}-side-list`);
      const domElement = document.getElementById(elemID);
      if (domElement && domListElement) {
        timeOutListScroll = setTimeout(
          () => domListElement.scrollTo(0, domElement.offsetTop),
          500,
        );
      }
    }
    return () => {
      // this will clear setTimeout
      timeOutListScroll && clearTimeout(timeOutListScroll);
    };
  }, []);

  useEffect(() => {
    if (list) {
      // refreshActions();
      // setShowSearch(false);
      setShowFilter(false);

      // Initial Sorting
      const sortedList = getSortedFilteredList(list);
      setRenderList(sortedList);

      // prepeare option for filter
      if (initFilter) {
        // get unique options by property
        let result = prepearFilteredOptions(list, initFilter);
        if (filterProps == "role") {
          result = result?.map(elOt => {
            return {
              label: USER_TYPES_OPTIONS(elOt?.label)?.toLocaleLowerCase(),
              value: elOt?.value,
            };
          });
        }
        setFilterOptions(result);
      }
    }
  }, [list, i18n?.language]);

  const setFirstElemInArray = firstElem => {
    if (firstElem) {
      const firstElemID = getObjId(firstElem);
      currentItemAction(firstElemID);
      setActiveListItem(firstElemID);
      history.push({ pathname: `/${type}/${firstElemID}` });
    }
  };

  const refreshFilter = () => {
    setShowSearch(false);
    setShowFilter(false);
    setSearchVal("");
    setFilter([]);
    const sortedList = getInitSortedList(list);
    const firstElem = sortedList?.length > 0 && sortedList[0];
    setFirstElemInArray(firstElem);
    setRenderList(sortedList);
  };

  const getInitSortedList = useCallback(
    (list, sortBy = null) => {
      const initList = list || [];
      const sortData = sortBy || sort || initSort;
      return initList?.length > 0 ? sortList(initList, sortData) : initList;
    },
    [sort, initSort, list],
  );

  const getSortedFilteredList = useCallback(
    initArray => {
      if (initArray?.length > 0) {
        if (filter?.length > 0) {
          if (searchVal) {
            const newList = filterListBy(initArray, initFilter, filter);
            const sortedList = sortList(
              searchListBy(newList, searchVal, initSearch),
              sort,
            );
            return sortedList;
          } else {
            const newList = filterListBy(initArray, initFilter, filter);
            const sortedList = sortList(newList, sort);
            return sortedList;
          }
        } else {
          if (searchVal) {
            const sortedList = sortList(
              searchListBy(initArray, searchVal, initSearch),
              sort,
            );
            return sortedList;
          } else {
            const sortedList = sortList(initArray, sort);
            return sortedList;
          }
        }
      } else {
        return [];
      }
    },
    [filter, sort, searchVal, renderList, initFilter],
  );

  const onSearch = e => {
    const searchBy = e.target.value;
    setSearchVal(searchBy);
    if (filter?.length > 0) {
      const newList = filterListBy(list, initFilter, filter);
      const sortedList = sortList(
        searchListBy(newList, searchVal, initSearch),
        sort,
      );
      const firstElem = sortedList?.length > 0 && sortedList[0];
      setFirstElemInArray(firstElem);
      setRenderList(sortedList);
    } else {
      const sortedList = sortList(
        searchListBy(list, searchBy, initSearch),
        sort,
      );
      const firstElem = sortedList?.length > 0 && sortedList[0];
      setFirstElemInArray(firstElem);
      setRenderList(sortedList);
    }
  };
  const onCloseSearch = useCallback(() => {
    setShowSearch(!showSearch);
    setSearchVal("");
    // reset list
    if (filter?.length > 0) {
      const newList = filterListBy(list, initFilter, filter);
      const sortedList = getInitSortedList(newList);
      const firstElem = sortedList?.length > 0 && sortedList[0];
      setFirstElemInArray(firstElem);
      setRenderList(sortedList);
    } else {
      const sortedList = getInitSortedList(list);
      const firstElem = sortedList?.length > 0 && sortedList[0];
      setFirstElemInArray(firstElem);
      setRenderList(sortedList);
    }
  }, [filter, list, showSearch, sort, initFilter]);

  const toggleFilter = useCallback(() => {
    setShowFilter(prev => !prev);
    // reset filter list
    /*  if (!showFilter === false) {
      setFilter([]);
      setRenderList(sortList(list, sort));
    } */
  }, []);

  const onSort = () => {
    const newSort = {
      ...sort,
      ascend: !sort.ascend,
    };
    setSort(newSort);
    const sortedList = getInitSortedList(renderList, newSort);
    const firstElem = sortedList?.length > 0 && sortedList[0];
    setFirstElemInArray(firstElem);
    setRenderList(sortedList);
  };

  const onAddAction = () => {
    setShowSearch(false);
    setShowFilter(false);
    setSearchVal("");
    setSort(initSort);
    setFilter([]);
    const sortedList = getInitSortedList(list, initSort);
    const firstElem = sortedList?.length > 0 && sortedList[0];
    setFirstElemInArray(firstElem);
    setRenderList(sortedList);
    addAction();
  };

  // change active list item
  const changeCurrentItem = (elementId, element) => {
    if (activeListItem !== elementId) {
      setActiveListItem(elementId);
      currentItemAction(elementId);
    }
  };

  const onFilter = checkedValues => {
    setFilter(checkedValues);

    if (checkedValues && checkedValues.length > 0 && initFilter) {
      const newList = filterListBy(list, initFilter, checkedValues);
      const sortedList = getInitSortedList(newList);
      const firstElem = sortedList?.length > 0 && sortedList[0];
      setFirstElemInArray(firstElem);
      setRenderList(sortedList);
    } else {
      const sortedList = getInitSortedList(list);
      const firstElem = sortedList?.length > 0 && sortedList[0];
      setFirstElemInArray(firstElem);
      setRenderList(sortedList);
    }
  };

  const sortFilterGroup = group => {
    if (group[0].key == "subject.name") {
      return group?.sort((a, b) =>
        a?.subject?.name.localeCompare(b?.subject?.name),
      );
    } else {
      return group;
    }
  };

  const filterRenderItem = useCallback((opt, inx, arr) => {
    if (opt.hasOwnProperty("group")) {
      return (
        <Collapse
          key={`group-${opt.group[0].key}`}
          bordered={false}
        >
          {/* <Checkbox key={opt.group[0].value} value={opt.group[0].value}>
          {opt.group[0].label}
        </Checkbox> */}
          <Panel header={opt.group[0].label}>
            {opt?.group &&
              sortFilterGroup(opt?.group)?.map((elm, indx) =>
                (indx !== 0 && elm) && (
                  <Checkbox
                    key={`${elm?.value}-option-${
                      opt.group[0].key
                    }`}
                    value={elm?.value}
                  >
                    {elm?.label}
                  </Checkbox>
                ),
              )}
          </Panel>
        </Collapse>
      );
    } else {
      return (
        <Checkbox key={`${opt.value}-option`} value={opt.value}>
          {opt.label}
        </Checkbox>
      );
    }
  }, []);
  const filterRender = useMemo(
    () =>
      filterOptions?.length > 0 && (
        <div
          key={`filter-by-list-${type}`}
          className="extra-bar-item filter-extra"
        >
          <CustomIcons
            className={`filter-extra__icon ${
              showFilter || filter?.length > 0 ? "show" : ""
            }`}
            type="filter"
            onClick={toggleFilter}
          />
          <div className={`filter-extra__bar ${showFilter ? "show" : ""}`}>
            <Checkbox.Group name={`filter`} onChange={onFilter} value={filter}>
              {filterOptions?.map(filterRenderItem)}
            </Checkbox.Group>
            {filter?.length > 0 && (
              <Button onClick={refreshFilter} className="reset-filter">
                <UndoOutlined /> {t("inputs.resetFilter")}
              </Button>
            )}
          </div>
        </div>
      ),
    [filterOptions, showFilter, filter, type],
  );

  // extra actions for side header
  const getExtraActions = useCallback(() => { 
    const extraActions = [
      <div key={`search-by-list-${type}`} className="extra-bar-item search-extra">
        <CustomIcons
          className={`search-extra__icon ${
            !showSearch || searchVal?.length > 0 ? "show" : ""
          }`}
          onClick={() => {
            setShowSearch(!showSearch);
            setShowFilter(false);
            searchRef.current.focus({
              cursor: "start",
            });
          }}
          type="search"
        />
        <span className={`search-extra__bar ${showSearch ? "show" : ""}`}>
          <Input
            size="large"
            placeholder={`${t("common.search")}...`}
            prefix={<CustomIcons type="search" />}
            suffix={<CloseOutlined onClick={onCloseSearch} />}
            onChange={onSearch}
            value={searchVal}
            ref={searchRef}
          />
        </span>
      </div>,
      filterRender,
      <div
        key={`sort-by-list-${type}`}
        className="extra-bar-item sort-extra"
        onClick={onSort}
      >
        <CustomIcons
          className={`${sort.ascend ? "ascend" : "descend"}`}
          type="sort"
        />
      </div>,
      <div
        key={`add-new-${type}`}
        className="extra-bar-item add-new-extra"
        onClick={onAddAction}
      >
        <PlusOutlined />
      </div>,
    ];
    return extraActions;
  }, [searchRef, searchVal, showSearch, filterRender, sort.ascend, type, i18n?.language]);

  const getSubTitleLabel = useCallback(type => {
    switch (type) {
      case "users":
      case "utilisateurs":
        return i18n?.language == "de" ? t("labels.users") : t("labels.users")?.toLocaleLowerCase();

      case "etudiants":
        return i18n?.language == "de" ? t("labels.students") : t("labels.students")?.toLocaleLowerCase();
      
      case "classes":
        return i18n?.language == "de" ? t("labels.classes") : t("labels.classes")?.toLocaleLowerCase();

      case "сours":
      case "сourses":
      case "courses":
        return i18n?.language == "de" ? t("labels.courses") : t("labels.courses")?.toLocaleLowerCase();
      
      case "news":
      case "communications":
        return t("labels.communications")?.toLocaleLowerCase();

      default:
        return type;
    }
  }, [i18n?.language]);

  return (
    <Sider className={childClassNames} onClick={onClick} {...rest}>
      <PageHeader
        className={`side-list-header ${showSearch ? "searchIsShowing" : ""}`}
        title={(!!title && title) || t("labels.listOf")}
        subTitle={
          !!subTitle
            ? subTitle
            : `${renderList?.length || 0} ${getSubTitleLabel(type)}`
        }
        extra={getExtraActions()}
      />

      <List
        header=""
        key={`${type}-side-list`}
        id={`${type}-side-list`}
        className="side-list"
        itemLayout="vertical"
        size="large"
        dataSource={renderList}
        // render each list element
        renderItem={
          outputListItem ||
          ((item, index) =>
            item?._id && (
              <SideListItem
                key={item?._id}
                type={type}
                onClickAction={changeCurrentItem}
                element={item}
                activeElement={activeListItem}
                searchString={searchVal}
              />
            ))
        }
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("errors.noData")}
            />
          ),
        }}
      />
    </Sider>
  );
}

SideList.defaultProps = {
  type: "utilisateurs",
  subTitle: "",
  ascend: true,
};
SideList.propTypes = {
  type: PropTypes.oneOf([
    "utilisateurs",
    "classes",
    "сours",
    "etudiants",
    "absence",
  ]).isRequired,
  list: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.string.isRequired,
    PropTypes.array.isRequired,
    PropTypes.object.isRequired,
  ]),
  sortProps: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  searchProps: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  currentItem: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.number.isRequired,
    PropTypes.string.isRequired,
  ]),
  ascend: PropTypes.bool,
  currentItemAction: PropTypes.func.isRequired,
  addAction: PropTypes.func.isRequired,
};

export default memo(SideList);
